/**
 * HYPSEYA — Night-Feed Mode
 * 2–4 minute one-handed practices for the 3am feed. Near-dark UI, huge touch
 * targets, no bright light. Part of the Matrescence (postpartum) pillar.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_PRACTICES = [
  { id: 'wave_breath', name: 'Wave Breathing', duration_sec: 120,
    description: 'Breathe in for 4, out for 6. Your exhale tells your nervous system: we are safe.',
    steps: ['Settle your shoulders down', 'In through the nose — 4 counts', 'Out slowly — 6 counts', 'Let the baby feel your calm rhythm'] },
  { id: 'anchor_touch', name: 'Anchor Touch', duration_sec: 180,
    description: 'One point of contact. Your attention rests where your body touches the chair.',
    steps: ['Notice where your body is supported', 'Let that point hold your weight fully', 'Each time the mind drifts, return to the contact point', 'You do not need to do anything else'] },
  { id: 'three_am_kindness', name: '3AM Kindness', duration_sec: 150,
    description: 'A practice for the hardest hour. Words for yourself, not for anyone else.',
    steps: ['Silently: this is hard, and I am doing it', 'Tiredness is not failure', 'This moment will pass — I do not have to fix it', 'One slow breath before you continue'] },
  { id: 'body_scan_micro', name: 'Micro Body Scan', duration_sec: 240,
    description: 'Four zones, one minute each. Jaw, shoulders, hands, belly.',
    steps: ['Unclench the jaw — let it hang', 'Shoulders melt away from ears', 'Soften the grip of your free hand', 'Let the belly be round and loose'] },
];

const AMBER = 'rgba(196,134,106,0.85)';
const AMBER_DIM = 'rgba(196,134,106,0.4)';

export default function NightFeedMode({ userId, onClose }) {
  const [practices, setPractices] = useState(FALLBACK_PRACTICES);
  const [active, setActive] = useState(null);      // practice object
  const [remaining, setRemaining] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/matrescence/night-feed/practices`)
      .then(r => { if (r.data?.practices?.length) setPractices(r.data.practices); })
      .catch(() => {});
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const start = (p) => {
    setActive(p);
    setRemaining(p.duration_sec);
    setStepIndex(0);
    setFinished(false);
    const stepEvery = Math.max(20, Math.floor(p.duration_sec / p.steps.length));
    timerRef.current = setInterval(() => {
      setRemaining(r => {
        const next = r - 1;
        const elapsed = p.duration_sec - next;
        setStepIndex(Math.min(p.steps.length - 1, Math.floor(elapsed / stepEvery)));
        if (next <= 0) {
          clearInterval(timerRef.current);
          setFinished(true);
          if (userId) {
            axios.post(`${API}/matrescence/night-feed/log`, {
              user_id: userId, practice_id: p.id, duration_sec: p.duration_sec,
            }).catch(() => {});
          }
          return 0;
        }
        return next;
      });
    }, 1000);
  };

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setActive(null);
    setFinished(false);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: '#070404',
      overflowY: 'auto',
      padding: '32px 22px 60px',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      <div style={{ maxWidth: 420, margin: '0 auto' }}>

        {/* Practice list */}
        {!active && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <p style={{ margin: 0, fontSize: '0.68rem', color: AMBER_DIM, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600 }}>
                Night-feed mode
              </p>
              <button onClick={onClose} style={{
                background: 'rgba(255,255,255,0.04)', border: 'none', color: 'rgba(255,255,255,0.35)',
                borderRadius: 100, padding: '10px 20px', fontSize: '0.85rem', cursor: 'pointer',
              }}>
                Exit
              </button>
            </div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400,
              fontSize: '1.4rem', color: 'rgba(255,255,255,0.75)', margin: '0 0 6px', lineHeight: 1.3,
            }}>
              You are awake.<br />You are not alone in this hour.
            </h2>
            <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', margin: '0 0 26px', lineHeight: 1.6 }}>
              One-handed practices, dim light. Pick one — the baby can stay right where they are.
            </p>

            {practices.map(p => (
              <button
                key={p.id}
                onClick={() => start(p)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  background: 'rgba(196,134,106,0.05)',
                  border: '1px solid rgba(196,134,106,0.15)',
                  borderRadius: 18, padding: '20px 20px', marginBottom: 12,
                  cursor: 'pointer', outline: 'none',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>{p.name}</span>
                  <span style={{ fontSize: '0.75rem', color: AMBER_DIM }}>{Math.round(p.duration_sec / 60)} min</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.55 }}>
                  {p.description}
                </p>
              </button>
            ))}
          </>
        )}

        {/* Active practice */}
        {active && !finished && (
          <div style={{ textAlign: 'center', paddingTop: 30 }}>
            <p style={{ fontSize: '0.68rem', color: AMBER_DIM, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, margin: '0 0 40px' }}>
              {active.name}
            </p>

            {/* Slow pulse circle */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 44 }}>
              <div style={{
                width: 140, height: 140, borderRadius: '50%',
                border: `1px solid ${AMBER_DIM}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'nfPulse 10s ease-in-out infinite',
              }}>
                <span style={{ fontSize: '1.6rem', color: AMBER, fontWeight: 300, letterSpacing: '0.05em' }}>
                  {fmt(remaining)}
                </span>
              </div>
            </div>
            <style>{`@keyframes nfPulse {
              0%,100% { transform: scale(0.94); opacity: 0.75; }
              50% { transform: scale(1.05); opacity: 1; }
            }`}</style>

            <p style={{
              fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6,
              fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400,
              minHeight: 60, padding: '0 10px', transition: 'opacity 0.5s',
            }}>
              {active.steps[stepIndex]}
            </p>

            <button onClick={stop} style={{
              marginTop: 40, background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem', cursor: 'pointer', padding: 16,
            }}>
              End early
            </button>
          </div>
        )}

        {/* Finished */}
        {active && finished && (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <div style={{ fontSize: '1.6rem', color: AMBER, marginBottom: 24 }}>✿</div>
            <p style={{
              fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400,
              fontSize: '1.25rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, margin: '0 0 10px',
            }}>
              Done. That was real care —<br />for both of you.
            </p>
            <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', margin: '0 0 40px' }}>
              Logged quietly. Go gently.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setActive(null)} style={{
                background: 'rgba(196,134,106,0.1)', border: '1px solid rgba(196,134,106,0.3)',
                color: AMBER, borderRadius: 100, padding: '13px 26px',
                fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              }}>
                Another practice
              </button>
              <button onClick={onClose} style={{
                background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.4)', borderRadius: 100, padding: '13px 26px',
                fontSize: '0.85rem', cursor: 'pointer',
              }}>
                Back to sleep
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
